import { alpha, Theme, Typography } from '@mui/material';
import { RepoTypes } from 'common-types';
import { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import SeverityBox from '../../../common/components/SeverityBox';
import DateFormat from '../../../elements/Date/DateFormat';
import ApplicationCell from '../../../elements/TableCells/ApplicationCell';
import { ArtifactInfo } from '../../types/artifacts-type';
import { getArtifactIconType } from '../../utils/artifacts-utils';
import InfoIconSection from './InfoIconSection';
import InfoItemsWrapper from './InfoItemsWrapper';
import Section from './Section';

const ArtifactGeneralInfo: FC<ArtifactGeneralInfoProps> = ({ artifact }) => {
  const { classes } = useStyles();
  const ArtifactTypeIcon = getArtifactIconType(artifact.artifactInfo.type);
  const name = artifact?.artifactInfo.name;
  const version = artifact?.artifactInfo.version;
  const hash = artifact?.artifactInfo.hash || '';
  const artifactType = artifact?.artifactInfo?.type;
  const appName = artifact.appDescription?.appName;
  const appType = artifact.appDescription?.appType as RepoTypes;
  const businessPriority = artifact.appDescription?.businessPriority;
  const created = artifact?.cicdDescription?.pipelineExecution?.createTime;
  const severitiesBreakDown = artifact.issuesBySeverity;

  return (
    <div className={classes.artifactGeneralInfo}>
      <InfoIconSection
        label='General Info'
        labelIcon='Artifact description: name, version, and unique identifier (such as hash or time of creation)'
      />
      <InfoItemsWrapper>
        <InfoIconSection
          label='Artifact Type'
          labelIcon='Artifact type describes the type of the underlying system running your code, such as container of server less function'>
          <div className={classes.artifactType}>
            {ArtifactTypeIcon && <ArtifactTypeIcon height='.8rem' />}
            <Typography variant='body1' color='text.primary'>
              {artifactType}
            </Typography>
          </div>
        </InfoIconSection>

        <InfoIconSection
          label='Name'
          labelIcon='The name of your artifact, as seen in your cloud provider'>
          {name}
        </InfoIconSection>

        <Section label={'Application'}>
          {appName && <ApplicationCell appName={appName} appType={appType} />}
        </Section>
        <Section label='Version/Tag'>{version}</Section>
        <Section label='Business Priority'>
          {businessPriority && Math.round(businessPriority)}
        </Section>
        <Section label='Hash'>{hash}</Section>

        <Section label='Created'>
          {created && (
            <DateFormat date={new Date(created)} color='text.primary' />
          )}
        </Section>

        <Section label='Severities' style={{ maxWidth: 500 }}>
          {severitiesBreakDown && (
            <SeverityBox
              severityBreakDown={severitiesBreakDown}
              className={classes.severityBox}
            />
          )}
        </Section>
      </InfoItemsWrapper>
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  artifactGeneralInfo: {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor: alpha(theme.palette.background.default, 0.5),
  },
  artifactType: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
  },

  severityBox: {
    display: 'block',
  },
}));

export interface ArtifactGeneralInfoProps {
  artifact: ArtifactInfo;
}

export default ArtifactGeneralInfo;
